import { useCallback, useEffect, useMemo, useState } from 'react'
import { Activity, AlertTriangle, Clock3, Layers3, RefreshCw, ServerCrash, Timer, Waves } from 'lucide-react'
import { liveOddsSupabase, propsSupabase } from '../lib/supabase'
import { getBookLogo } from '../lib/logoMaps'
import './FleetLatencyWidget.css'

const PROXY_PATH = '/api/oracle-vm'
const DIRECT_VM_BASE = 'http://170.9.62.134:8443'
const MANIFEST_PATH = '/data/expansionManifest.generated.json'
const PROPS_MANIFEST_PATH = '/data/expansionPropsManifest.generated.json'

function resolveEndpointUrl(endpoint) {
  if (typeof window !== 'undefined' && window.location.protocol === 'http:') {
    return `${DIRECT_VM_BASE}/${endpoint}`
  }
  return `${PROXY_PATH}?endpoint=${encodeURIComponent(endpoint)}`
}

async function fetchJsonWithTimeout(url, timeoutMs = 5000) {
  const controller = new AbortController()
  const timeoutId = setTimeout(() => controller.abort(), timeoutMs)

  try {
    const response = await fetch(url, {
      signal: controller.signal,
      headers: { Accept: 'application/json' },
    })
    const data = await response.json().catch(() => null)
    if (!response.ok) {
      throw new Error(data?.error || data?.message || `HTTP ${response.status}`)
    }
    return data
  } catch (error) {
    if (error.name === 'AbortError') throw new Error('Timed out after 5 seconds')
    throw error
  } finally {
    clearTimeout(timeoutId)
  }
}

function humanizeAge(dateStr) {
  if (!dateStr) return 'never'
  const diffMs = Date.now() - new Date(dateStr).getTime()
  if (!Number.isFinite(diffMs)) return 'unknown'
  if (diffMs < 60000) return `${Math.max(0, Math.floor(diffMs / 1000))}s ago`
  if (diffMs < 3600000) return `${Math.floor(diffMs / 60000)}m ago`
  if (diffMs < 86400000) return `${Math.floor(diffMs / 3600000)}h ago`
  return `${Math.floor(diffMs / 86400000)}d ago`
}

function computePercentile(values, percentile) {
  if (!values.length) return null
  const sorted = [...values].sort((a, b) => a - b)
  const index = Math.min(sorted.length - 1, Math.ceil((percentile / 100) * sorted.length) - 1)
  return sorted[index]
}

function classifyLane(book, receiverBooks, expansionBooks, propsBooks) {
  if (propsBooks.has(book)) return 'props'
  if (receiverBooks.has(book)) return 'receiver'
  if (book === 'Kalshi' || book === 'Polymarket') return 'worker'
  if (expansionBooks.has(book)) return 'expansion'
  return 'unknown'
}

function summarizeTableProbe(result) {
  if (!result) return { status: 'unknown', detail: 'Not checked' }
  if (result.ok) return { status: 'ready', detail: `${result.count ?? 0} rows` }
  return { status: 'missing', detail: result.error || 'Unavailable' }
}

export default function FleetLatencyWidget() {
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [receiverStats, setReceiverStats] = useState(null)
  const [statusRows, setStatusRows] = useState([])
  const [expansionManifest, setExpansionManifest] = useState(null)
  const [propsManifest, setPropsManifest] = useState(null)
  const [tableProbe, setTableProbe] = useState({})
  const [lastRefreshed, setLastRefreshed] = useState(null)

  const fetchData = useCallback(async () => {
    setLoading(true)

    const settled = await Promise.allSettled([
      fetchJsonWithTimeout(resolveEndpointUrl('stats')),
      fetchJsonWithTimeout(MANIFEST_PATH),
      fetchJsonWithTimeout(PROPS_MANIFEST_PATH),
      liveOddsSupabase
        ? liveOddsSupabase
            .from('scraper_status')
            .select('bookmaker,status,updated_at,last_success,delta_count,cycle_time_ms,last_error')
            .order('bookmaker')
        : Promise.resolve({ data: [], error: new Error('Supabase not configured') }),
      liveOddsSupabase
        ? liveOddsSupabase.from('scrape_cycles').select('id', { count: 'exact', head: true })
        : Promise.resolve({ count: null, error: new Error('Supabase not configured') }),
      propsSupabase
        ? propsSupabase.from('live_props').select('compound_key', { count: 'exact', head: true })
        : Promise.resolve({ count: null, error: new Error('Supabase not configured') }),
      propsSupabase
        ? propsSupabase.from('props_history').select('id', { count: 'exact', head: true })
        : Promise.resolve({ count: null, error: new Error('Supabase not configured') }),
      propsSupabase
        ? propsSupabase.from('props_book_status').select('bookmaker', { count: 'exact', head: true })
        : Promise.resolve({ count: null, error: new Error('Supabase not configured') }),
    ])

    const [
      statsResult,
      expansionResult,
      propsManifestResult,
      statusResult,
      scrapeCyclesResult,
      livePropsResult,
      propsHistoryResult,
      propsBookStatusResult,
    ] = settled

    const firstError =
      [statsResult, expansionResult, propsManifestResult].find((result) => result.status === 'rejected')?.reason

    setError(firstError ? (firstError.message || String(firstError)) : null)

    if (statsResult.status === 'fulfilled') setReceiverStats(statsResult.value)
    if (expansionResult.status === 'fulfilled') setExpansionManifest(expansionResult.value)
    if (propsManifestResult.status === 'fulfilled') setPropsManifest(propsManifestResult.value)
    if (statusResult.status === 'fulfilled') setStatusRows(statusResult.value.data || [])

    setTableProbe({
      scrape_cycles: scrapeCyclesResult.status === 'fulfilled'
        ? { ok: !scrapeCyclesResult.value.error, count: scrapeCyclesResult.value.count, error: scrapeCyclesResult.value.error?.message }
        : { ok: false, count: null, error: scrapeCyclesResult.reason?.message },
      live_props: livePropsResult.status === 'fulfilled'
        ? { ok: !livePropsResult.value.error, count: livePropsResult.value.count, error: livePropsResult.value.error?.message }
        : { ok: false, count: null, error: livePropsResult.reason?.message },
      props_history: propsHistoryResult.status === 'fulfilled'
        ? { ok: !propsHistoryResult.value.error, count: propsHistoryResult.value.count, error: propsHistoryResult.value.error?.message }
        : { ok: false, count: null, error: propsHistoryResult.reason?.message },
      props_book_status: propsBookStatusResult.status === 'fulfilled'
        ? { ok: !propsBookStatusResult.value.error, count: propsBookStatusResult.value.count, error: propsBookStatusResult.value.error?.message }
        : { ok: false, count: null, error: propsBookStatusResult.reason?.message },
    })

    setLastRefreshed(new Date())
    setLoading(false)
  }, [])

  useEffect(() => {
    fetchData()
    const interval = setInterval(fetchData, 60000)
    return () => clearInterval(interval)
  }, [fetchData])

  const receiverBooks = useMemo(
    () => new Set(Object.keys(receiverStats?.by_bookmaker || {})),
    [receiverStats]
  )

  const expansionBooks = useMemo(
    () => new Set((expansionManifest?.books || []).map((entry) => entry.bookmaker).filter(Boolean)),
    [expansionManifest]
  )

  const propsBooks = useMemo(
    () => new Set((propsManifest?.books || []).map((entry) => entry.bookmaker).filter(Boolean)),
    [propsManifest]
  )

  const fleetRows = useMemo(() => {
    const byStatus = new Map(statusRows.map((row) => [row.bookmaker, row]))
    const expansionByBook = new Map((expansionManifest?.books || []).map((entry) => [entry.bookmaker, entry]))
    const propsByBook = new Map((propsManifest?.books || []).map((entry) => [entry.bookmaker, entry]))
    const allBooks = new Set([
      ...receiverBooks,
      ...expansionBooks,
      ...propsBooks,
      'Kalshi',
      'Polymarket',
      ...statusRows.map((row) => row.bookmaker),
    ])

    return Array.from(allBooks)
      .filter(Boolean)
      .map((book) => {
        const status = byStatus.get(book)
        const expansion = expansionByBook.get(book)
        const props = propsByBook.get(book)
        const receiverCount = Number(receiverStats?.by_bookmaker?.[book] || 0)
        const lane = classifyLane(book, receiverBooks, expansionBooks, propsBooks)
        return {
          bookmaker: book,
          lane,
          receiverCount,
          expansionRows: expansion?.rows ?? expansion?.available_rows ?? 0,
          propsRows: props?.rows ?? 0,
          status: status?.status || (lane === 'receiver' || lane === 'worker' ? 'online' : 'expansion'),
          cycleTimeMs: Number(status?.cycle_time_ms) || null,
          deltaCount: Number(status?.delta_count) || 0,
          freshness: status?.last_success || status?.updated_at || null,
          lastError: status?.last_error || null,
        }
      })
      .sort((a, b) => a.bookmaker.localeCompare(b.bookmaker))
  }, [statusRows, expansionManifest, propsManifest, receiverStats, receiverBooks, expansionBooks, propsBooks])

  const onlineBooks = useMemo(
    () => fleetRows.filter((row) => ['online', 'expansion'].includes(row.status)).length,
    [fleetRows]
  )

  const cycleTimes = useMemo(
    () => fleetRows.map((row) => row.cycleTimeMs).filter((value) => Number.isFinite(value)),
    [fleetRows]
  )

  const averageCycle = useMemo(() => {
    if (!cycleTimes.length) return null
    return Math.round(cycleTimes.reduce((sum, value) => sum + value, 0) / cycleTimes.length)
  }, [cycleTimes])

  const p95Cycle = useMemo(() => computePercentile(cycleTimes, 95), [cycleTimes])
  const deltaSum = useMemo(() => fleetRows.reduce((sum, row) => sum + row.deltaCount, 0), [fleetRows])

  const slowestBooks = useMemo(
    () => [...fleetRows].filter((row) => row.cycleTimeMs != null).sort((a, b) => (b.cycleTimeMs || 0) - (a.cycleTimeMs || 0)).slice(0, 6),
    [fleetRows]
  )

  const noisiestBooks = useMemo(
    () => [...fleetRows].filter((row) => row.deltaCount > 0).sort((a, b) => b.deltaCount - a.deltaCount).slice(0, 6),
    [fleetRows]
  )

  return (
    <div className="widget flw-widget">
      <div className="widget-header">
        <h3 className="widget-title">
          <Clock3 size={16} />
          Fleet & Latency
          <span className="flw-badge">runtime telemetry</span>
        </h3>
        <button className="widget-refresh-btn" onClick={fetchData} title="Refresh fleet metrics">
          <RefreshCw size={14} className={loading ? 'flw-spinner' : ''} />
        </button>
      </div>

      {error && <div className="flw-error">{error}</div>}

      <div className="flw-summary">
        <div className="flw-stat-card">
          <span className="flw-stat-value">{receiverBooks.size || '—'}</span>
          <span className="flw-stat-label">receiver books live</span>
        </div>
        <div className="flw-stat-card">
          <span className="flw-stat-value">{fleetRows.length || '—'}</span>
          <span className="flw-stat-label">dashboard books tracked</span>
        </div>
        <div className="flw-stat-card">
          <span className="flw-stat-value">{averageCycle != null ? `${averageCycle}ms` : '—'}</span>
          <span className="flw-stat-label">avg current cycle</span>
        </div>
        <div className="flw-stat-card">
          <span className="flw-stat-value">{p95Cycle != null ? `${p95Cycle}ms` : '—'}</span>
          <span className="flw-stat-label">p95 current cycle</span>
        </div>
        <div className="flw-stat-card">
          <span className="flw-stat-value">{deltaSum.toLocaleString()}</span>
          <span className="flw-stat-label">delta_count sum</span>
        </div>
        <div className="flw-stat-card">
          <span className="flw-stat-value">{onlineBooks}</span>
          <span className="flw-stat-label">books reporting now</span>
        </div>
      </div>

      <div className="flw-grid">
        <section className="flw-panel">
          <div className="flw-panel-header">
            <span>Telemetry Readiness</span>
            <span className="flw-panel-meta">what the stack can measure today</span>
          </div>
          <div className="flw-readiness-list">
            {[
              ['scrape_cycles', summarizeTableProbe(tableProbe.scrape_cycles)],
              ['live_props', summarizeTableProbe(tableProbe.live_props)],
              ['props_history', summarizeTableProbe(tableProbe.props_history)],
              ['props_book_status', summarizeTableProbe(tableProbe.props_book_status)],
            ].map(([name, probe]) => (
              <div key={name} className={`flw-readiness-card ${probe.status}`}>
                <div className="flw-readiness-top">
                  <span className="flw-readiness-name">{name}</span>
                  <span className={`flw-readiness-badge ${probe.status}`}>{probe.status}</span>
                </div>
                <div className="flw-readiness-detail">{probe.detail}</div>
              </div>
            ))}
          </div>
          <div className="flw-footnote">
            `scraper_status` gives latest-cycle runtime and delta volume. True run interval and end-to-end latency require `scrape_cycles`.
          </div>
        </section>

        <section className="flw-panel">
          <div className="flw-panel-header">
            <span>Slowest Current Cycles</span>
            <span className="flw-panel-meta">latest measurable runtime</span>
          </div>
          <div className="flw-mini-list">
            {slowestBooks.length === 0 ? (
              <div className="flw-empty">No cycle telemetry yet</div>
            ) : slowestBooks.map((row) => (
              <div key={row.bookmaker} className="flw-mini-row">
                <div className="flw-book-name">
                  {getBookLogo(row.bookmaker) && (
                    <img src={getBookLogo(row.bookmaker)} alt="" className="flw-book-logo" onError={(event) => { event.target.style.display = 'none' }} />
                  )}
                  <span>{row.bookmaker}</span>
                </div>
                <div className="flw-mini-metrics">
                  <span>{row.cycleTimeMs}ms</span>
                  <span>{humanizeAge(row.freshness)}</span>
                </div>
              </div>
            ))}
          </div>
        </section>

        <section className="flw-panel">
          <div className="flw-panel-header">
            <span>Noisiest Books</span>
            <span className="flw-panel-meta">largest latest deltas</span>
          </div>
          <div className="flw-mini-list">
            {noisiestBooks.length === 0 ? (
              <div className="flw-empty">No delta telemetry yet</div>
            ) : noisiestBooks.map((row) => (
              <div key={row.bookmaker} className="flw-mini-row">
                <div className="flw-book-name">
                  {getBookLogo(row.bookmaker) && (
                    <img src={getBookLogo(row.bookmaker)} alt="" className="flw-book-logo" onError={(event) => { event.target.style.display = 'none' }} />
                  )}
                  <span>{row.bookmaker}</span>
                </div>
                <div className="flw-mini-metrics">
                  <span>{row.deltaCount.toLocaleString()} Δ</span>
                  <span>{row.cycleTimeMs != null ? `${row.cycleTimeMs}ms` : '—'}</span>
                </div>
              </div>
            ))}
          </div>
        </section>

        <section className="flw-panel flw-panel-wide">
          <div className="flw-panel-header">
            <span>Fleet Table</span>
            <span className="flw-panel-meta">current measurable state by book</span>
          </div>
          <div className="flw-table">
            <div className="flw-table-header">
              <span>Book</span>
              <span>Lane</span>
              <span>Status</span>
              <span>Rows</span>
              <span>Cycle</span>
              <span>Delta</span>
              <span>Freshness</span>
            </div>
            {fleetRows.map((row) => (
              <div key={row.bookmaker} className="flw-table-row">
                <div className="flw-book-name">
                  {getBookLogo(row.bookmaker) && (
                    <img src={getBookLogo(row.bookmaker)} alt="" className="flw-book-logo" onError={(event) => { event.target.style.display = 'none' }} />
                  )}
                  <span>{row.bookmaker}</span>
                </div>
                <span className={`flw-lane-pill ${row.lane}`}>{row.lane}</span>
                <span className={`flw-status-pill ${row.status}`}>{row.status}</span>
                <span>
                  {row.receiverCount > 0 ? row.receiverCount.toLocaleString() : row.expansionRows > 0 ? row.expansionRows.toLocaleString() : row.propsRows > 0 ? row.propsRows.toLocaleString() : '—'}
                </span>
                <span>{row.cycleTimeMs != null ? `${row.cycleTimeMs}ms` : '—'}</span>
                <span>{row.deltaCount ? row.deltaCount.toLocaleString() : '0'}</span>
                <span>{humanizeAge(row.freshness)}</span>
              </div>
            ))}
          </div>
          <div className="flw-footnote">
            Current widget uses `scraper_status`, receiver stats, and expansion manifests. After `scrape_cycles` is applied, add run interval, p95 by book, and true end-to-end latency.
          </div>
        </section>

        <section className="flw-panel flw-panel-wide">
          <div className="flw-panel-header">
            <span>Measurement Model</span>
            <span className="flw-panel-meta">what to add next</span>
          </div>
          <div className="flw-note-grid">
            <div className="flw-note-card">
              <Timer size={14} />
              <div>
                <div className="flw-note-title">Current runtime</div>
                <div className="flw-note-detail">Use `cycle_time_ms` from `scraper_status` for a latest-cycle runtime proxy.</div>
              </div>
            </div>
            <div className="flw-note-card">
              <Waves size={14} />
              <div>
                <div className="flw-note-title">Run cadence</div>
                <div className="flw-note-detail">Store one row per cycle in `scrape_cycles`, then compute average and p95 interval between `scrape_started_at` values.</div>
              </div>
            </div>
            <div className="flw-note-card">
              <Layers3 size={14} />
              <div>
                <div className="flw-note-title">Stage timings</div>
                <div className="flw-note-detail">Capture `scrape_started_at`, `scrape_finished_at`, `ingest_received_at`, `persisted_at`, and `broadcast_at` to break down pipeline latency.</div>
              </div>
            </div>
            <div className="flw-note-card">
              <ServerCrash size={14} />
              <div>
                <div className="flw-note-title">Failure tracking</div>
                <div className="flw-note-detail">Store `error_stage`, `error_message`, `service_name`, `service_version`, and `git_sha` to reach Fleet Manager-grade observability.</div>
              </div>
            </div>
          </div>
        </section>
      </div>

      <div className="widget-footer">
        <span>{lastRefreshed ? `Refreshed ${lastRefreshed.toLocaleTimeString()}` : 'Loading...'}</span>
        <span>`scrape_cycles` migration: 20260326_114149</span>
      </div>
    </div>
  )
}
